/* eslint-disable camelcase */

/*  MUI  */
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Typography
} from '@mui/material';

/*  Shared  */
import { PageTitle } from 'application/Shared/Components/App';
import { AdminBackButton } from 'application/Shared/Components/Buttons/BackButton';
import { CopyInternalUseId } from 'application/Shared/Components/CopyInternalUseId';
import { Helpers, Notify, Response } from 'application/Shared/Helpers';
import { Confirm } from 'application/Shared/Modals/Confirm';
import { Requests } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  React  */
import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { withRouter } from 'router/withRouter';

class ClientSettings extends Component {
    constructor(props) {
        super(props);
        this.state = {
            clientId: '',
            cw_clientId: '',
            identityTenantId: '',
            loaded: false,
            security_logging: false,
            tenantId: ''
        };

        /* requests */
        this.requests = new Requests();
    }

    componentDidMount() {
        const { match } = this.props;
        const { clientId } = match.params;
        this.setState({ clientId }, () => {
            this.load();
        });
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = async () => {
        const settings = await this.getSettings();
        this.loadTenant({ settings });
    };

    unload = () => {
        this.requests.cancel();
    };

    loadTenant = async (p) => {
        if (this.unmount) {
            return;
        }

        const { t } = this.props;
        const { clientId } = this.state;
        const { settings } = p;

        const params = {};
        if (clientId) {
            params.id = clientId;
            params.includeClients = true;
        }

        try {
            const response = await axios.get('/tenant', {
                cancelToken: this.requests.token(),
                params
            });
            if (!Response.hasData(response)) {
                debug.e('No Tenant Found', response);
                Notify.e(t('Common.error'));
                this.setState({ ...settings, loaded: true });
            }

            const tenant = Response.getOne(response);
            this.setState({
                ...settings,
                identityTenantId: tenant.identityTenantId,
                loaded: true,
                tenantId: tenant.id
            });
        } catch (e) {
            debug.e('Tenant called failed', e);
            this.setState({
                loaded: true
            });
        }
    };

    getSettings = async () => {
        const { clientId } = this.state;

        /* params */
        const params = {};
        if (clientId) {
            params.platformTenantId = clientId;
            params.includeClients = true;
        }

        const fields = this.getFields();
        const settings = {};

        // TODO: Fix - SAAS-994
        // eslint-disable-next-line no-restricted-syntax
        for (const field of fields) {
            const { type, key } = field;

            if (type !== 'display') {
                try {
                    // TODO: Fix - SAAS-994
                    // eslint-disable-next-line no-await-in-loop
                    const response = await axios.get(`/tenantSetting/${key}`, {
                        cancelToken: this.requests.token(),
                        params
                    });

                    if (Helpers.isset(response, 'data', 'data', 0, 'value')) {
                        settings[key] = response.data.data[0].value;
                    } else {
                        debug.w('Failed to get Setting', response);
                    }
                } catch (e) {
                    debug.w('Error getting Settings axios call', e);
                }
            }
        }

        return settings;
    };

    getFields = () => {
        const { t } = this.props;

        return [
            {
                key: 'cw_clientId',
                label: t('ClientSettings.cw_clientId'),
                label_description: t('ClientSettings.cw_clientId_description'),
                type: 'text'
            },
            {
                key: 'security_logging',
                label: t('ClientSettings.security_logging'),
                label_description: t(
                    'ClientSettings.security_logging_description'
                ),
                type: 'boolean'
            },
            {
                key: 'hidePersonalEmail',
                label: t('ClientSettings.hide_personal_details'),
                label_description: t(
                    'ClientSettings.hide_personal_details_description'
                ),
                type: 'boolean'
            },
            {
                key: 'identityTenantId',
                label: t('ClientSettings.identity_internalID'),
                label_description: t(
                    'ClientSettings.identity_internalID_description'
                ),
                type: 'display'
            },
            {
                key: 'tenantId',
                label: t('ClientSettings.tenantReferenceId'),
                label_description: t(
                    'ClientSettings.tenantReferenceId_description'
                ),
                type: 'display'
            }
        ];
    };

    save = async () => {
        const { t } = this.props;
        const st = this.state;
        const { clientId } = st;
        const fields = this.getFields();
        const err = t('TenantSettings.unableToSave');
        let shouldShowRetroactiveRedactionConfirmation = false;

        try {
            // TODO: Fix - SAAS-994
            // eslint-disable-next-line no-restricted-syntax
            for (const field of fields) {
                if (field.type !== 'display') {
                    const params = {
                        key: field.key,
                        value: st[field.key]
                    };

                    // If the user toggles hidePersonalEmail on, then we want to show them a Confirmation modal
                    // to see if they would like to retroactively redact personal emails as well.
                    if (field.key === 'hidePersonalEmail' && !!params.value) {
                        shouldShowRetroactiveRedactionConfirmation = true;
                    }

                    if (clientId) {
                        params.platformTenantId = clientId;
                    }

                    // TODO: Fix - SAAS-994
                    // eslint-disable-next-line no-await-in-loop
                    await axios.post('/tenantSetting/', params);
                }
            }
        } catch (e) {
            debug.e(err, e);
            Notify.e(err);
            return;
        }

        Notify.s(t('TenantSettings.saved'));

        this.setState({
            showRetroactiveRedactionConfirmation:
                shouldShowRetroactiveRedactionConfirmation
        });
    };

    redact = async () => {
        const { t } = this.props;
        const { tenantId } = this.state;
        try {
            await axios.post(`/tenant/redactRetroactively`, {
                id: tenantId
            });

            Notify.s(
                t('ClientSettings.hidePersonalDetails.redactionSubmitted')
            );
        } catch (e) {
            debug.e(e.toString());
            Notify.e(t('ClientSettings.hidePersonalDetails.redactionError'));
        }

        this.setState({ showRetroactiveRedactionConfirmation: false });
    };

    renderField(field) {
        const st = this.state;
        const { key, label, type } = field;
        const description = field.label_description;
        const value = st[key];

        return (
            <Grid item xs={12} key={key} style={{ marginBottom: '16px' }}>
                {type === 'boolean' && (
                    <Box display="flex" alignItems="flex-start">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    data-testid={`adminUtilitiesClientSettings${key}`}
                                    checked={value}
                                    onChange={(e) =>
                                        this.setState({
                                            [key]: e.target.checked
                                        })
                                    }
                                    label={label}
                                    style={{
                                        paddingBottom: 0,
                                        paddingTop: 0
                                    }}
                                />
                            }
                            label={
                                <Box display="flex" flexDirection="column">
                                    <Typography
                                        variant="p"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography variant="p">
                                        {description}
                                    </Typography>
                                </Box>
                            }
                            style={{
                                alignItems: 'flex-start',
                                display: 'flex'
                            }}
                        />
                    </Box>
                )}
            </Grid>
        );
    }

    render() {
        const { t } = this.props;

        const {
            cw_clientId,
            hidePersonalEmail,
            identityTenantId,
            loaded,
            showRetroactiveRedactionConfirmation,
            tenantId
        } = this.state;
        const fields = this.getFields();

        return (
            <>
                <PageTitle title={t('ClientSettings.title')} />
                <AdminBackButton linkTo="/configure" />
                {loaded && (
                    <Paper elevation={0} variant="outlined">
                        <Grid
                            container
                            spacing={0}
                            data-testid="adminUtilitiesClientSettingsPage"
                        >
                            <Grid item xs={12} p={1}>
                                <Box mb="8px">
                                    <Typography variant="h5">
                                        {t('HubSettings.tenantDetails')}
                                    </Typography>
                                </Box>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    marginTop="16px"
                                >
                                    <Typography
                                        variant="p"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {t('ClientSettings.cw_clientId')}
                                    </Typography>
                                    <Box display="flex" alignItems="flex-end">
                                        <TextField
                                            inputProps={{
                                                'data-testid':
                                                    'adminUtilitiesClientSettingsCwClientId'
                                            }}
                                            onChange={(e) => {
                                                this.setState({
                                                    cw_clientId: e.target.value
                                                });
                                            }}
                                            style={{ width: 'fit-content' }}
                                            value={cw_clientId}
                                        />
                                        <Typography variant="p" ml="4px">
                                            {t(
                                                'ClientSettings.cw_clientId_description'
                                            )}
                                        </Typography>
                                    </Box>
                                </Box>
                                <CopyInternalUseId
                                    title={t(
                                        'ClientSettings.identity_internalID'
                                    )}
                                    value={identityTenantId}
                                />
                                <CopyInternalUseId
                                    title={t(
                                        'ClientSettings.tenantReferenceId'
                                    )}
                                    value={tenantId}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <hr />
                            </Grid>
                            <Grid item xs={12} p={1}>
                                <Typography
                                    variant="h5"
                                    style={{ paddingY: 0 }}
                                >
                                    {t(
                                        'HubSettings.tenantConfigurationSettings'
                                    )}
                                </Typography>
                                {fields.map((field) => this.renderField(field))}
                            </Grid>
                        </Grid>

                        <hr />

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            marginBottom="8px"
                            marginTop="16px"
                        >
                            <Button
                                data-testid="adminUtilitiesClientSettingsSaveBtn"
                                data-trackid="admin_utilities_settings_save"
                                color="primary"
                                onClick={() => this.save()}
                                variant="contained"
                            >
                                {t('Common.save_settings')}
                            </Button>
                        </Box>

                        {hidePersonalEmail &&
                            showRetroactiveRedactionConfirmation && (
                                <Confirm
                                    modalOpen={
                                        !!showRetroactiveRedactionConfirmation
                                    }
                                    promptConfirmFn={this.redact}
                                    promptCancelFn={() =>
                                        this.setState({
                                            showRetroactiveRedactionConfirmation: false
                                        })
                                    }
                                    promptTitle={t(
                                        'ClientSettings.hidePersonalDetails.confirmation.title'
                                    )}
                                    promptText={
                                        <Box>
                                            <Typography
                                                fontWeight={600}
                                                marginBottom="8px"
                                            >
                                                {t(
                                                    'ClientSettings.hidePersonalDetails.confirmation.descriptionHeading'
                                                )}
                                            </Typography>
                                            <Typography>
                                                {t(
                                                    'ClientSettings.hidePersonalDetails.confirmation.descriptionBody'
                                                )}
                                            </Typography>
                                        </Box>
                                    }
                                />
                            )}
                    </Paper>
                )}
            </>
        );
    }
}
export default withRouter(withTranslation()(ClientSettings));
